/**
 * offlineCache.ts
 * オフラインキャッシュ関連メソッド
 * App.vue から切り出し
 */

import store from '@/store'
import { PMTiles } from 'pmtiles'
import {
  updateDragHandles,
  getBBoxFromPolygon,
} from '@/js/downLoad'
import { createSquarePolygonAtCenter } from '@/js/pyramid'
import { showSuccess } from '@/js/utils/messageHelper'

type VM = any

export async function cachesCrear() {
    await caches.delete('raster-tile-cache');
    await caches.delete('vector-tile-cache');
    showSuccess('クリアしました。')
}

export async function downloadTiles(vm: VM) {
  vm.s_dialogForOffline2 = false
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'タイルダウンロード中')
  let bbox = getBBoxFromPolygon(vm.$store.state.featureForOfflineBbox)

  if (!navigator.serviceWorker.controller) {
    alert('サービスワーカーがアクティブではありません。ページをリロードしてください。');
    return;
  }

  const pmtilesUrl = 'https://kenzkenz.net/pmtiles/2025final3.pmtiles'; // ベースURL（末尾に /z/x/y.pbf が付く想定）
  const rasterUrl = 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'

  /** BBOX＋ズーム範囲内のタイル枚数を計算 */
  function countRasterTiles(bbox: number[], minZoom: number, maxZoom: number) {
    let count = 0;
    for (let z = minZoom; z <= maxZoom; z++) {
      const [minX, minY] = lngLatToTile([bbox[0], bbox[3]], z);
      const [maxX, maxY] = lngLatToTile([bbox[2], bbox[1]], z);
      const tilesInZ = (maxX - minX + 1) * (maxY - minY + 1);
      count += tilesInZ;
    }
    return count;
  }
  /**
   * 緯度経度 → Google XYZ タイル座標に変換
   */
  function lngLatToTile([lng, lat]: number[], z: number) {
    const n = 2 ** z;
    const x = Math.floor((lng + 180) / 360 * n);
    const y = Math.floor(
        (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) /
        2 *
        n
    );
    return [x, y];
  }

  /**
   * BBOX＋ズーム範囲だけ GSI のラスタータイルをキャッシュ
   * @param bbox [minLng, minLat, maxLng, maxLat]
   * @param minZoom 最小ズーム（例: 14）
   * @param maxZoom 最大ズーム（例: 16）
   * @param template URL テンプレート（デフォルトは GSI の標準地図）
   */
  async function cacheRasterTiles(
      bbox: number[],
      minZoom: number,
      maxZoom: number,
      template = 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'
  ) {
    // Service Worker 側と合わせたキャッシュ名
    const CACHE_NAME = 'raster-tile-cache';
    const cache = await caches.open(CACHE_NAME);
    for (let z = minZoom; z <= maxZoom; z++) {
      // BBOX の NW→SE でタイル範囲を算出
      const [minX, minY] = lngLatToTile([bbox[0], bbox[3]], z);
      const [maxX, maxY] = lngLatToTile([bbox[2], bbox[1]], z);

      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          // URL を生成
          const url = template
              .replace('{z}', String(z))
              .replace('{x}', String(x))
              .replace('{y}', String(y));
          try {
            // fetch すると SW の CacheFirst 戦略で自動キャッシュ
            const res = await fetch(url);
            if (!res.ok) {
              console.warn(`タイル取得失敗 ${z}/${x}/${y}`, res.status);
              continue;
            }
            // 必要なら明示的に put しても OK
            await cache.put(url, res.clone());
            vm.tileCount++
            store.commit('setLoadingMessage', `${vm.tileCount}/${vm.totalTileCount}`)
          } catch (e) {
            console.error(`Error fetching raster ${z}/${x}/${y}`, e);
          }
        }
      }
    }
  }
  vm.totalTileCount = countRasterTiles(bbox, 2, 17)
  await cacheRasterTiles(bbox, 2, 17,rasterUrl)

  /**
   * BBOX＋ズーム範囲だけ pmtiles を部分キャッシュ
   * @param pmtilesUrl: PMTiles ファイルの URL
   * @param bbox: [minLng, minLat, maxLng, maxLat]
   * @param minZoom: 最小ズーム
   * @param maxZoom: 最大ズーム
   */
  async function cachePmtilesBuffers(pmtilesUrl: string, bbox: number[], minZoom: number, maxZoom: number) {
    const pmtiles = new PMTiles(pmtilesUrl);

    // 必要ならヘッダを先読み（内部でインデックスを準備）
    await pmtiles.getHeader(); // ※open() は不要 :contentReference[oaicite:2]{index=2}

    // Service Worker 側と同じキャッシュ名
    const cache = await caches.open("vector-tile-cache");

    for (let z = minZoom; z <= maxZoom; z++) {
      // BBOX からタイルの x,y 範囲を算出
      const [minX, minY] = lngLatToTile([bbox[0], bbox[3]], z);
      const [maxX, maxY] = lngLatToTile([bbox[2], bbox[1]], z);

      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          // タイルデータを取得
          const tileResp = await pmtiles.getZxy(z, x, y);
          if (!tileResp) continue; // タイル未存在ならスキップ

          // ArrayBuffer → Response に変換してキャッシュへ登録
          const tileRequest = new Request(`${pmtilesUrl}/${z}/${x}/${y}.pbf`);
          const tileResponse = new Response(tileResp.data, {
            headers: { "Content-Type": "application/x-protobuf" }
          });
          await cache.put(tileRequest, tileResponse);
          vm.tileCount++
          store.commit('setLoadingMessage', `${vm.tileCount}/${vm.totalTileCount}`)
        }
      }
    }
  }
  vm.tileCount = 0
  vm.totalTileCount = countRasterTiles(bbox, 14, 16)
  await cachePmtilesBuffers(pmtilesUrl, bbox, 14, 16)

  store.commit('setLoading2', false)
  showSuccess('タイルのダウンロードが完了しました！');
}

export function bboxPolygon(vm: VM) {
  vm.s_dialogForOffline = false
  const properties = {
    id: 'offlineBbox',
    color: 'rgba(0,0,0,0.1)'
  }
  createSquarePolygonAtCenter(10, properties)
  updateDragHandles(true)
  const map01 = vm.$store.state.map01
  map01.easeTo({
    zoom: 10,
    duration: 1000,       // ミリ秒（例：1秒）
    easing: (t: number) => t        // イージング関数（ここでは線形）
  });
}

